import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  Button,
  VStack,
  InputGroup,
  InputLeftElement,
  Input,
  Text,
  Flex,
  HStack,
  Box,
  Divider,
  Avatar,
  Kbd,
  Spinner,
} from '@chakra-ui/react'
import { SearchIcon, ArrowForwardIcon } from '@chakra-ui/icons'
import { skipToken } from '@reduxjs/toolkit/query'
import { useState, useMemo, useEffect, useCallback } from 'react'
import { useAppSelector } from '../../../app/typedReduxHooks'
import type { PrescriberLocationProfile } from '../../../types/location'
import { useLocationSearch } from '../hooks/useLocationSearch'
import { useGetPrescriberLocationProfilesQuery } from '../prescriberLocationProfilesSlice'

const WNSW_ORGANIZATION_ID = process.env.REACT_APP_WNSW_ORGANIZATION_ID

interface SelectLocationModalProps {
  isOpen: boolean
  prescriberName: string
  title: string
  prescriberNumber: string
  onSelect: (prescriberProfileId: string) => void
  onJoinNew: () => void
}

function getInitialProfileId(
  profiles: PrescriberLocationProfile[],
  blockDefaultLocation: boolean
): string | null {
  if (profiles.length === 0) return null
  if (blockDefaultLocation) return null
  const defaultProfile = profiles.find((profile) => profile.isDefault)
  return defaultProfile?.id ?? profiles[0].id
}

export const SelectLocationModal = ({
  isOpen,
  prescriberName,
  title,
  prescriberNumber,
  onSelect,
  onJoinNew,
}: SelectLocationModalProps) => {
  const organizationId = useAppSelector(
    (state) => state.organization?.data?.id ?? state.prescriber.prescriber?.organization?.id
  )

  const { data: prescriberProfiles = [], isLoading } = useGetPrescriberLocationProfilesQuery(
    isOpen ? undefined : skipToken
  )
  const joinedProfiles = useMemo(
    () => prescriberProfiles.filter((profile) => profile.joined),
    [prescriberProfiles]
  )
  const hasDefaultProfile = joinedProfiles.some((profile) => profile.isDefault)
  const hasNonDefaultProfile = joinedProfiles.some((profile) => !profile.isDefault)
  const blockDefaultLocation =
    !!WNSW_ORGANIZATION_ID &&
    organizationId === WNSW_ORGANIZATION_ID &&
    hasDefaultProfile &&
    hasNonDefaultProfile

  const { searchQuery, setSearchQuery, filteredLocations } = useLocationSearch(joinedProfiles)
  const selectableLocations = useMemo(
    () => filteredLocations.filter((location) => !blockDefaultLocation || !location.isDefault),
    [filteredLocations, blockDefaultLocation]
  )

  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null)

  useEffect(() => {
    setSelectedProfileId((prev) => {
      const prevProfile = joinedProfiles.find((profile) => profile.id === prev)
      if (prevProfile && (!blockDefaultLocation || !prevProfile.isDefault)) return prev
      return getInitialProfileId(joinedProfiles, blockDefaultLocation)
    })
  }, [joinedProfiles, blockDefaultLocation])

  const selectedLocation =
    joinedProfiles.find((profile) => profile.id === selectedProfileId) ?? null

  const handleStartSession = useCallback(() => {
    if (selectedProfileId) onSelect(selectedProfileId)
  }, [selectedProfileId, onSelect])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen || isLoading || selectableLocations.length === 0) return

      const currentIndex = selectableLocations.findIndex(
        (location) => location.id === selectedProfileId
      )

      if (e.key === 'ArrowDown') {
        e.preventDefault()
        const nextIndex = currentIndex < selectableLocations.length - 1 ? currentIndex + 1 : 0
        setSelectedProfileId(selectableLocations[nextIndex].id)
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        const prevIndex = currentIndex > 0 ? currentIndex - 1 : selectableLocations.length - 1
        setSelectedProfileId(selectableLocations[prevIndex].id)
      } else if (e.key === 'Enter') {
        e.preventDefault()
        handleStartSession()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, isLoading, selectableLocations, selectedProfileId, handleStartSession])

  const getInitials = (name: string) => {
    if (name.length >= 2) {
      return `${name[0]}${name[name.length - 1]}`.toUpperCase()
    }
    return name.substring(0, 2).toUpperCase()
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {}}
      size="lg"
      isCentered
      closeOnOverlayClick={false}
      closeOnEsc={false}
    >
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent borderRadius="2xl" overflow="hidden" boxShadow="2xl" bg="white">
        <Box bg="blue.900" color="white" px={8} py={8}>
          <HStack spacing={4} mb={8}>
            <Avatar
              size="md"
              name={prescriberName}
              bg="blue.500"
              color="white"
              fontWeight="bold"
              getInitials={() => getInitials(prescriberName)}
            />
            <VStack align="start" spacing={0}>
              <Text fontWeight="bold" fontSize="md">
                {title} {prescriberName}
              </Text>
              <Text fontSize="sm" color="gray.400" fontWeight="medium">
                Prescriber {prescriberNumber}
              </Text>
            </VStack>
          </HStack>

          <Text fontSize="xl" fontWeight="bold" mb={2} letterSpacing="tight">
            Where are you working today?
          </Text>
          <Text fontSize="sm" color="gray.400" fontWeight="medium">
            Select your location to start your session
          </Text>
        </Box>

        <ModalBody px={8} py={6}>
          {isLoading ? (
            <Flex justify="center" align="center" minH="200px">
              <Spinner size="lg" color="blue.500" thickness="4px" />
            </Flex>
          ) : (
            <VStack spacing={4} align="stretch">
              <InputGroup size="md">
                <InputLeftElement pointerEvents="none" color="gray.400">
                  <SearchIcon />
                </InputLeftElement>
                <Input
                  placeholder="Search locations..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  borderRadius="lg"
                  borderColor="blue.400"
                  borderWidth="2px"
                  _hover={{ borderColor: 'blue.500' }}
                  _focus={{ borderColor: 'blue.500', boxShadow: 'none' }}
                  fontWeight="medium"
                />
              </InputGroup>

              <Flex
                justify="space-between"
                align="center"
                fontSize="sm"
                color="gray.400"
                fontWeight="medium"
                pt={1}
              >
                <HStack spacing={4}>
                  <HStack spacing={1.5}>
                    <Kbd bg="white" color="gray.500" borderColor="gray.200" fontSize="xs">
                      ↑
                    </Kbd>
                    <Kbd bg="white" color="gray.500" borderColor="gray.200" fontSize="xs">
                      ↓
                    </Kbd>
                    <Text>navigate</Text>
                  </HStack>
                  <HStack spacing={1.5}>
                    <Kbd bg="white" color="gray.500" borderColor="gray.200" fontSize="xs" px={2}>
                      ↵
                    </Kbd>
                    <Text>select</Text>
                  </HStack>
                </HStack>
                <Text>
                  {filteredLocations.length} location{filteredLocations.length !== 1 ? 's' : ''}
                </Text>
              </Flex>

              <VStack
                align="stretch"
                spacing={2}
                maxH="220px"
                overflowY="auto"
                css={{
                  '&::-webkit-scrollbar': { width: '4px' },
                  '&::-webkit-scrollbar-track': { width: '6px' },
                  '&::-webkit-scrollbar-thumb': {
                    background: 'var(--chakra-colors-gray-200)',
                    borderRadius: '24px',
                  },
                }}
              >
                {filteredLocations.map((location) => {
                  const isSelected = selectedProfileId === location.id
                  const canSelect = !blockDefaultLocation || !location.isDefault
                  return (
                    <Flex
                      key={location.id}
                      p={4}
                      borderWidth="1px"
                      borderColor={isSelected ? 'blue.100' : 'gray.200'}
                      borderRadius="lg"
                      bg={isSelected ? 'blue.50' : 'white'}
                      cursor={canSelect ? 'pointer' : 'not-allowed'}
                      opacity={canSelect ? 1 : 0.5}
                      onClick={() => canSelect && setSelectedProfileId(location.id)}
                      align="center"
                      justify="space-between"
                      _hover={canSelect ? { bg: isSelected ? 'blue.50' : 'gray.50' } : undefined}
                      transition="all 0.2s"
                      aria-disabled={!canSelect}
                    >
                      <Text
                        fontWeight="bold"
                        fontSize="sm"
                        color={isSelected ? 'blue.600' : 'gray.700'}
                      >
                        {location.name}
                      </Text>
                      <HStack spacing={4}>
                        {location.isDefault && (
                          <Box
                            bg="teal.50"
                            color="teal.700"
                            px={3}
                            py={1}
                            borderRadius="full"
                            fontSize="xs"
                            fontWeight="bold"
                          >
                            Default
                          </Box>
                        )}
                        <Text fontSize="sm" color="gray.400" fontWeight="medium">
                          {location.suburb}
                        </Text>
                        <Box
                          w={5}
                          h={5}
                          borderRadius="full"
                          borderWidth="2px"
                          borderColor={isSelected ? 'blue.400' : 'gray.300'}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          bg={isSelected ? 'blue.400' : 'transparent'}
                        >
                          {isSelected && <Box w={2} h={2} borderRadius="full" bg="white" />}
                        </Box>
                      </HStack>
                    </Flex>
                  )
                })}
              </VStack>
            </VStack>
          )}
        </ModalBody>

        <Box px={8} pb={6}>
          <Divider borderColor="gray.200" mb={6} />

          <VStack align="stretch" spacing={4} mb={6}>
            <VStack align="start" spacing={0}>
              <Text fontSize="xs" fontWeight="bold" color="gray.400" letterSpacing="wider">
                SELECTED
              </Text>
              <Text fontWeight="bold" fontSize="lg" color="gray.900">
                {selectedLocation ? selectedLocation.name : 'None'}
              </Text>
            </VStack>
            <Flex justify="center">
              <Button
                colorScheme="blue"
                size="md"
                onClick={handleStartSession}
                isDisabled={!selectedLocation || isLoading}
                rightIcon={<ArrowForwardIcon />}
                px={8}
                borderRadius="lg"
                fontWeight="bold"
                width="full"
              >
                Start session
              </Button>
            </Flex>
          </VStack>

          <Divider borderColor="gray.200" mb={4} />

          <Text fontSize="sm" color="gray.500" textAlign="center" fontWeight="medium">
            Can't find your location?{' '}
            <Button
              variant="link"
              color="blue.500"
              onClick={onJoinNew}
              fontWeight="bold"
              fontSize="sm"
            >
              Join a new location
            </Button>
          </Text>
        </Box>
      </ModalContent>
    </Modal>
  )
}
